import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { Icons } from '../../../../shared/helpers/icons';
import { ActivatedRoute, Router } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { ParentInfoComponent } from "./parent-info/parent-info.component";
import { ParentThreatKnowledgeComponent } from "./parent-threat-knowledge/parent-threat-knowledge.component";
import { FamilyRelationshipComponent } from './family-relationship/family-relationship.component';
import { SchoolAdjustmentComponent } from './school-adjustment/school-adjustment.component';
import { ProblemBehaviorComponent } from "./problem-behavior/problem-behavior.component";
import { ExposureViolenceComponent } from "./exposure-violence/exposure-violence.component";
import { AccessWeaponComponent } from './access-weapon/access-weapon.component';
import { CharacteristicsComponent } from './characteristics/characteristics.component';
import { StudentHistoryComponent } from "./student-history/student-history.component";
import { PeerRelationshipComponent } from './peer-relationship/peer-relationship.component';
import { OthersComponent } from './others/others.component';
import { ParentInterviewService } from '../../../../shared/services/parentInterview.service';
import { UserDataService } from '../../../../shared/services/auth/userData.service';
import { IParentInterview } from '../../../../shared/models/parentInterview';
import { ParentInterviewOfficialSignatureComponent } from "./parent-interview-official-signature/parent-interview-official-signature.component";
import { ParentInterviewSubmissionComponent } from "./parent-interview-submission/parent-interview-submission.component";
import { PopupMessageComponent } from '../../../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../../../components/spinner/spinner.component';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'parent-interview',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    ParentInfoComponent,
    ParentThreatKnowledgeComponent,
    FamilyRelationshipComponent,
    SchoolAdjustmentComponent,
    ProblemBehaviorComponent,
    ExposureViolenceComponent,
    AccessWeaponComponent,
    CharacteristicsComponent,
    StudentHistoryComponent,
    StudentHistoryComponent,
    PeerRelationshipComponent,
    OthersComponent,
    ParentInterviewOfficialSignatureComponent,
    ParentInterviewSubmissionComponent,
    PopupMessageComponent,
    SpinnerComponent
],
  templateUrl: './parent-interview.component.html',
  styleUrl: './parent-interview.component.css'
})
export class ParentInterviewComponent {

  icons = Icons;
  currentBuildingId: string | null = null;

  currentCaseId: string | null = null;
  currentParentInterview: any | null = null;
  stepsConfig = [
    {
      id: 1,
      completionProperty: 'student_and_parent_information_completion',
      nextStepDependency: null
    },
    {
      id: 2,
      completionProperty: 'knowledge_of_threat_completion',
      nextStepDependency: 'student_and_parent_information_completion'
    },
    {
      id: 3,
      completionProperty: 'school_adjustment_completion',
      nextStepDependency: 'knowledge_of_threat_completion'
    },
    {
      id: 4,
      completionProperty: 'family_relationship_and_stressors_completion',
      nextStepDependency: 'school_adjustment_completion'
    },
    {
      id: 5,
      completionProperty: 'peer_relationships_completion',
      nextStepDependency: 'family_relationship_and_stressors_completion'
    },
    {
      id: 6,
      completionProperty: 'problem_behavior_completion',
      nextStepDependency: 'peer_relationships_completion'
    },
    {
      id: 7,
      completionProperty: 'access_to_weapons_completion',
      nextStepDependency: 'problem_behavior_completion'
    },
    {
      id: 8,
      completionProperty: 'exposure_to_violence_completion',
      nextStepDependency: 'access_to_weapons_completion'
    },
    {
      id: 9,
      completionProperty: 'history_completion',
      nextStepDependency: 'exposure_to_violence_completion'
    },
    {
      id: 10,
      completionProperty: 'characteristics_completion',
      nextStepDependency: 'history_completion'
    },
    {
      id: 11,
      completionProperty: 'summary_completion',
      nextStepDependency: 'characteristics_completion'
    },
    {
      id: 12,
      completionProperty: 'official_signatures_completion',
      nextStepDependency: 'summary_completion'
    }
  ];
  finalStepConfig = {
      id: 13,
      completionProperty: 'parental_interview_completion',
      nextStepDependency: 'official_signatures_completion'
  };
  currentStep: number = 1;
  tooltips = ['Parent Information', 'Knowledge of Threat', 'School Adjustment', 'Family Relationship', 'Peer Relationship', 'Problem Behavior', 'Access to Weapons or Dangerous Items', 'Exposure to Violence', 'History', 'Health and Personal Characteristics', 'Additional Information', 'Official Signature', 'Submission'];
  messageModal: string | null = null;
  popupMessage: string | null = null;

  loading: boolean = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private parentInterviewService: ParentInterviewService,
    private userDataService: UserDataService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    
    this.route.paramMap.subscribe((params) => {
      this.currentCaseId = params.get('caseId');
    });
    
    this.loadParentInterview();
  }

  loadParentInterview(): void {
    if (this.currentCaseId) {
      this.loading = true;
      this.parentInterviewService.getParentInterviewByCaseId(this.currentCaseId).subscribe({
        next: (response) => {
          if (response) {
            this.currentParentInterview = response;
          } else {
            this.currentParentInterview = null;
          }
          this.loading = false;
          return
        },
        error: (error) => {
          this.loading = false;
          console.error('Error:', error);
          this.messageModal = 'unsuccess';
          this.popupMessage = error.message;  
        }
      });
    }
  }

  handleUpdateParentInterview(data: IParentInterview, section: number): void {
    if (this.currentCaseId && this.currentBuildingId) {
      this.loading = true;
      const fullData = data;
      fullData.case_id = this.currentCaseId!;
      fullData.section = section;
      fullData.building_id = Number(this.currentBuildingId);
      this.parentInterviewService.updateParentInterview(fullData).subscribe({
        next: (response) => {
          this.loading = false;
          this.loadParentInterview();
          this.toastr.success('Saved successful!', 'Success');
          this.currentStep = this.currentStep != this.finalStepConfig.id ? section + 1 : this.currentStep;
          if (section == 13 ) {
            this.router.navigate(['/assessments/']);
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error updating Parent Interview:', error.message);
          this.toastr.error(error.message, 'Error');
        }
      });
    }
  }

  onBack(): void {
    this.router.navigate(['/assessments/']);
  }

  // move to next section
  goToStep(stepId: number): void {
    if (this.canMoveToStep(stepId)) {
      this.currentStep = stepId;
    } else {
      console.error('You must complete the required steps before proceeding.');
    }
  }
  // check if they already completed the required step before allowing them to move to the next step
  canMoveToStep(stepId: number): boolean {
    let stepConfig;
    if (stepId === this.finalStepConfig.id) {
      stepConfig = this.finalStepConfig; // Handle the final step separately
    } else {
      stepConfig = this.stepsConfig.find(step => step.id === stepId);
    }
  
    if (!stepConfig) {
      console.error('Step configuration not found for step', stepId);
      return false;
    }
  
    if (stepConfig.nextStepDependency && this.currentParentInterview) {
      return this.currentParentInterview?.[stepConfig.nextStepDependency] === 1;
    }
  
    return true;
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }
}
